import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { AppFormDialog } from '@/components/apps/AppFormDialog';
import { CampaignFormDialog } from '@/components/campaigns/CampaignFormDialog';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/queryKeys';
import type { AppRecord, Campaign } from '@/lib/types';

async function fetchApps(): Promise<AppRecord[]> {
  const { data } = await api.get<AppRecord[]>('/v1/apps');
  return data;
}

async function fetchCampaigns(): Promise<Campaign[]> {
  // ?status=all so an existing draft / paused / archived campaign still counts
  // as "you have campaigns" for the wizard.
  const { data } = await api.get<Campaign[]>('/v1/campaigns', {
    params: { status: 'all' },
  });
  return data;
}

interface StepProps {
  index: number;
  title: string;
  description: string;
  done: boolean;
  locked?: boolean;
  action: React.ReactNode;
}

function Step({ index, title, description, done, locked, action }: StepProps) {
  return (
    <Card className={locked ? 'opacity-60' : ''}>
      <CardHeader className="flex flex-row items-start gap-4">
        <div
          className={`mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-full text-sm font-semibold ${
            done
              ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-200'
              : 'bg-muted text-muted-foreground'
          }`}
          aria-hidden
        >
          {done ? <Check className="size-4" /> : index}
        </div>
        <div className="flex-1">
          <CardTitle className="text-base">{title}</CardTitle>
          <CardDescription>{description}</CardDescription>
        </div>
      </CardHeader>
      <CardContent>{action}</CardContent>
    </Card>
  );
}

export default function Welcome() {
  const navigate = useNavigate();
  const apps = useQuery({ queryKey: queryKeys.apps, queryFn: fetchApps });
  const campaigns = useQuery({
    queryKey: queryKeys.campaigns('all'),
    queryFn: fetchCampaigns,
  });
  const [appDialogOpen, setAppDialogOpen] = useState(false);
  const [campaignDialogOpen, setCampaignDialogOpen] = useState(false);

  const hasApp = (apps.data?.length ?? 0) > 0;
  const hasCampaign = (campaigns.data?.length ?? 0) > 0;
  const allDone = hasApp && hasCampaign;

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <header className="space-y-2">
        <h1 className="text-2xl font-semibold">Welcome to Pulse</h1>
        <p className="text-sm text-muted-foreground">
          Three quick steps to start tracking installs. You can revisit any of
          these later from the sidebar.
        </p>
      </header>

      <Step
        index={1}
        title="Register your first app"
        description="Tell us the store URLs and bundle IDs for the app you want to track. You can add more apps later."
        done={hasApp}
        action={
          hasApp ? (
            <p className="text-sm text-muted-foreground">
              {apps.data?.length} app{apps.data?.length === 1 ? '' : 's'}{' '}
              registered.{' '}
              <Link to="/apps" className="underline">
                Manage apps
              </Link>
              .
            </p>
          ) : (
            <Button onClick={() => setAppDialogOpen(true)}>
              Register app
              <ArrowRight />
            </Button>
          )
        }
      />

      <Step
        index={2}
        title="Grab your API keys"
        description="A publishable key for your mobile binary and a secret key for your server. Rotate them anytime."
        done={hasApp}
        locked={!hasApp}
        action={
          <Button asChild variant="outline" disabled={!hasApp}>
            <Link to="/settings/keys">View API keys</Link>
          </Button>
        }
      />

      <Step
        index={3}
        title="Create your first campaign"
        description="Generates a tracking URL, short URL, QR code, and copy-paste snippets you can drop into ads, emails, or print."
        done={hasCampaign}
        locked={!hasApp}
        action={
          hasCampaign ? (
            <p className="text-sm text-muted-foreground">
              {campaigns.data?.length} campaign
              {campaigns.data?.length === 1 ? '' : 's'} created.{' '}
              <Link to="/campaigns" className="underline">
                View campaigns
              </Link>
              .
            </p>
          ) : (
            <Button
              onClick={() => setCampaignDialogOpen(true)}
              disabled={!hasApp}
            >
              Create campaign
              <ArrowRight />
            </Button>
          )
        }
      />

      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={() => navigate('/dashboard')}>
          Skip for now
        </Button>
        <Button
          onClick={() => navigate('/dashboard')}
          disabled={!allDone}
        >
          {allDone ? 'Finish — go to dashboard' : 'Complete all steps to continue'}
        </Button>
      </div>

      <AppFormDialog
        open={appDialogOpen}
        onOpenChange={setAppDialogOpen}
        editing={null}
      />
      <CampaignFormDialog
        open={campaignDialogOpen}
        onOpenChange={setCampaignDialogOpen}
        apps={apps.data ?? []}
      />
    </div>
  );
}
